/**
 * Проигрывание.
 *
 * Через Web Animations API, а не записью в style каждый кадр. Причины
 * практические, не вкусовые:
 *
 *   — `finished` это промис, а не событие. Уход попапа перестаёт быть
 *     подпиской на animationend, которая в jsdom вообще не срабатывает,
 *     потому что react-dom не вешает слушателя без window.AnimationEvent;
 *   — `cancel()` сам возвращает элемент в исходное состояние: не нужен
 *     снимок инлайн-стилей и его восстановление;
 *   — `playbackRate` меняет скорость одной строкой, в том числе на лету;
 *   — модуль не пишет в element.style на каждом кадре и потому не воюет
 *     с фреймворком.
 */

import type { Sheet } from './bake'

export type Direction = 'in' | 'out'

/** Какой канал листа читать. См. раскладку каналов в bake.ts. */
export type Channel = 'content' | 'rim'

export interface Playback {
  finished: Promise<void>
  cancel: () => void
  /** Домотать до конца, не дожидаясь. Нужно при прерывании перехода. */
  finish: () => void
}

const noop: Playback = {
  finished: Promise.resolve(),
  cancel: () => {},
  finish: () => {},
}

/**
 * Повесить маску на элемент.
 *
 * Слой содержимого читает яркость, слой подтёка — альфу. Это не две разные
 * настройки одного и того же: в одной картинке лежат два разных фронта,
 * и режим маски выбирает, какой именно. Клиент, не знающий mask-mode,
 * прочитает оба слоя как альфу — ободок пропадёт, раскрытие останется верным.
 */
export function applyMask(
  el: HTMLElement,
  sheet: Sheet,
  channel: Channel,
  offsetY: string,
  stretchY: string,
): void {
  const url = `url(${sheet.url})`
  const size = `${sheet.frames * 100}% ${stretchY}`
  const s = el.style

  s.maskImage = url
  s.webkitMaskImage = url
  s.maskRepeat = 'no-repeat'
  s.webkitMaskRepeat = 'no-repeat'
  s.maskSize = size
  s.webkitMaskSize = size
  s.maskPosition = `0% ${offsetY}`
  s.webkitMaskPosition = `0% ${offsetY}`
  s.maskMode = channel === 'content' ? 'luminance' : 'alpha'
}

export function clearMask(el: HTMLElement): void {
  const s = el.style
  s.maskImage = ''
  s.webkitMaskImage = ''
  s.maskSize = ''
  s.webkitMaskSize = ''
  s.maskPosition = ''
  s.webkitMaskPosition = ''
  s.maskMode = ''
}

export interface SpreadOptions {
  frames: number
  duration: number
  direction: Direction
  offsetY: string
  /** Сдвиг во времени. Отрицательный — начать раньше. */
  delay?: number
  drift?: { x: string; y: string }
}

/**
 * Прогон листа.
 *
 * Шагов на один меньше, чем кадров, и это не описка. Ширина листа —
 * N×100% элемента, а позиция в процентах выравнивает доли, а не левый край:
 * смещение равно pos×(W − N·W). Значит кадр k стоит на позиции k/(N−1).
 *
 * jump-none обязателен. У steps(n, end) последнее значение достигается
 * только в момент завершения, то есть финальный прирост появляется
 * мгновенным скачком — переход заканчивается хлопком вместо шага.
 */
export function spread(el: HTMLElement, opts: SpreadOptions): Playback {
  if (typeof el.animate !== 'function') return noop

  const { frames, duration, direction, offsetY, delay = 0, drift } = opts
  const from = `0% ${offsetY}`
  const to = `100% ${offsetY}`

  const animations = [
    el.animate(
      [
        { maskPosition: from, webkitMaskPosition: from },
        { maskPosition: to, webkitMaskPosition: to },
      ] as Keyframe[],
      {
        duration,
        delay,
        easing: `steps(${Math.max(1, frames - 1)}, jump-none)`,
        fill: 'both',
        direction: direction === 'out' ? 'reverse' : 'normal',
      },
    ),
  ]

  if (drift) {
    // Снос идёт отдельной анимацией и по своей кривой: он про инерцию,
    // а маска про ступени. Одной кривой на двоих не бывает.
    animations.push(
      el.animate(
        [
          { transform: `translate3d(${drift.x}, ${drift.y}, 0) scale(1.03)` },
          { transform: 'translate3d(0, 0, 0) scale(1)' },
        ],
        {
          duration,
          delay,
          easing: direction === 'out' ? 'cubic-bezier(0.4,0,1,1)' : 'cubic-bezier(0.16,1,0.3,1)',
          fill: 'both',
          direction: direction === 'out' ? 'reverse' : 'normal',
        },
      ),
    )
  }

  return {
    finished: Promise.all(animations.map((a) => a.finished)).then(() => undefined),
    cancel: () => animations.forEach((a) => a.cancel()),
    finish: () => animations.forEach((a) => a.finish()),
  }
}

/**
 * Дыхание — единственный механизм без маски.
 *
 * Размывается содержимое, а не форма, поэтому запечь его нельзя: поле
 * времени прибытия описывает, ЧТО открылось, а не насколько оно чёткое.
 * Отсюда и ограничения — радиус упёрт в потолок, а сам механизм пускается
 * только на малых площадях: стоимость размытия растёт с площадью,
 * и на полном экране оно дороже всей остальной анимации вместе взятой.
 */
export function breathe(
  el: HTMLElement,
  duration: number,
  direction: Direction,
  side: number,
): Playback {
  if (typeof el.animate !== 'function') return noop

  const blur = Math.max(4, Math.min(13, side / 34))
  const anim = el.animate(
    [
      { opacity: 0, filter: `blur(${blur}px)`, transform: 'scale(1.045)' },
      { opacity: 1, filter: 'blur(0px)', transform: 'scale(1)' },
    ],
    {
      duration,
      easing: 'cubic-bezier(0.16,1,0.3,1)',
      fill: 'both',
      direction: direction === 'out' ? 'reverse' : 'normal',
    },
  )

  return {
    finished: anim.finished.then(() => undefined),
    cancel: () => anim.cancel(),
    finish: () => anim.finish(),
  }
}

/** Затухание: спокойный режим и клиенты без масок. */
export function fade(el: HTMLElement, duration: number, direction: Direction): Playback {
  if (typeof el.animate !== 'function') return noop

  const anim = el.animate([{ opacity: 0 }, { opacity: 1 }], {
    duration,
    easing: 'linear',
    fill: 'both',
    direction: direction === 'out' ? 'reverse' : 'normal',
  })

  return {
    finished: anim.finished.then(() => undefined),
    cancel: () => anim.cancel(),
    finish: () => anim.finish(),
  }
}
